const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const getCookieValue = (name: string): string => {
  if (typeof document === "undefined") return "";

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));
  if (!cookie) return "";

  const value = cookie.split("=").slice(1).join("=");
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getAuthValue = (...keys: string[]): string => {
  for (const key of keys) {
    const cookieValue = getCookieValue(key);
    if (cookieValue) return cookieValue;

    const storageValue = localStorage.getItem(key);
    if (storageValue) return storageValue;
  }
  return "";
};

export interface InstantiateDesignResponse {
  asset_folder_id?: string;
  design_id: string;
  design_title?: string;
  folder_id?: string;
  folder_name?: string;
  message?: string;
  reused_existing_draft?: boolean;
  upload_parent_id?: string;
}

export async function instantiateDesignFromTemplate(
  templateId: string,
  vendorId: string
): Promise<InstantiateDesignResponse> {
  if (!templateId) {
    throw new Error("Template id is required");
  }
  if (!vendorId) {
    throw new Error("Vendor id is required");
  }

  const token = getAuthValue("sessionToken");
  const userId = getAuthValue("userId", "user_id");

  if (!token) {
    throw new Error("Session token not found");
  }
  if (!userId) {
    throw new Error("User id not found");
  }

  const response = await fetch(
    `${API_BASE_URL}/realesy-studio/${encodeURIComponent(templateId)}/instantiate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: userId,
        vendor_id: vendorId,
        parent_template_id: templateId,
        status: "draft",
      }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Failed to instantiate design (${response.status})`);
  }

  const data = (await response.json()) as InstantiateDesignResponse;
  if (!data?.design_id) {
    throw new Error("Design id not returned from instantiate API");
  }

  return data;
}

/** Filename from Content-Disposition (RFC 5987 filename* and basic filename=). */
function parseFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const star = /filename\*=(?:UTF-8''|)([^;\n]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      return star[1].trim().replace(/^["']|["']$/g, "");
    }
  }
  const basic = /filename=(?:"([^"]+)"|([^;\s]+))/i.exec(header);
  const raw = basic?.[1] ?? basic?.[2];
  if (raw) return raw.trim();
  return undefined;
}

/**
 * Downloads a Work Drive file by id (same id stored on order line items as `preview_url`).
 * GET {API_BASE}/work-drive/files/{fileId}/download with Bearer session token.
 */
export async function downloadWorkDriveFile(fileId: string, suggestedFileName?: string): Promise<void> {
  const id = String(fileId ?? "").trim();
  if (!id) {
    throw new Error("File id is required");
  }

  const token = getAuthValue("sessionToken");
  if (!token) {
    throw new Error("Session token not found");
  }

  const response = await fetch(
    `${API_BASE_URL}/work-drive/files/${encodeURIComponent(id)}/download`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || `Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const fromHeader = parseFilenameFromContentDisposition(response.headers.get("Content-Disposition"));
  const fileName = fromHeader || suggestedFileName || "custom-design";

  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** API root already includes `/api/v1` when using REACT_APP_API_URL from env. */
const customizationApiUrl = (path: string) => {
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

export type CustomizationContextSource =
  | "signage_create_order"
  | "signage_add_installation";

export interface CreateCustomizationContextRequest {
  source: CustomizationContextSource;
  product_id: string;
  variant_id: string;
  /** After `instantiateDesignFromTemplate`, pass the returned `design_id` here (Studio open-design id). */
  template_id: string;
  line_item_key: string;
  mode: "create" | "edit";
  /** Optional order snapshot. If empty or not provided it will be omitted from the payload. */
  order?: Record<string, unknown>;
  /** Optional installation snapshot. If empty or not provided it will be omitted from the payload. */
  installation_order?: Record<string, unknown>;
  /** When re-entering studio for the same line, pass prior context id. */
  context_id?: string;
  /**
   * When customizing an additional product in the same session, pass the batch id from the
   * first Studio return (`?batch_id=`). Omit on the first customization so the API creates a new batch.
   */
  batch_id?: string;
}

export interface CreateCustomizationContextResponse {
  batch_id: string;
  context_id: string;
}

/**
 * POST /api/v1/customization-contexts — create batch + context for Studio handoff.
 */
export async function createCustomizationContext(
  body: CreateCustomizationContextRequest
): Promise<CreateCustomizationContextResponse> {
  const token = getAuthValue("sessionToken");
  if (!token) {
    throw new Error("Session token not found");
  }

  // Build payload but omit empty order / installation_order fields to keep payload minimal.
  const payload: Record<string, unknown> = {
    source: body.source,
    product_id: body.product_id,
    variant_id: body.variant_id,
    template_id: body.template_id,
    line_item_key: body.line_item_key,
    mode: body.mode,
  };
  if (body.context_id) payload.context_id = body.context_id;
  const batchIdForRequest = String(body.batch_id ?? "").trim();
  if (batchIdForRequest) payload.batch_id = batchIdForRequest;
  if (body.order && Object.keys(body.order).length > 0) payload.order = body.order;
  if (body.installation_order && Object.keys(body.installation_order).length > 0)
    payload.installation_order = body.installation_order;

  const response = await fetch(customizationApiUrl("/customization-contexts"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text().catch(() => "");
  if (!response.ok) {
    throw new Error(text || `Failed to create customization context (${response.status})`);
  }

  let data: CreateCustomizationContextResponse;
  try {
    data = text ? (JSON.parse(text) as CreateCustomizationContextResponse) : ({} as CreateCustomizationContextResponse);
  } catch {
    throw new Error("Invalid response from customization context API");
  }
  if (!data?.batch_id || !data?.context_id) {
    throw new Error("batch_id or context_id not returned from customization context API");
  }
  return data;
}

/** A single context result stored inside a customization batch. */
export interface CustomizationContextResult {
  context_id: string;
  /** The open-design id that was opened/edited in Studio. */
  design_id?: string;
  /** Preview image id (workdrive file id). */
  preview_id?: string;
  /** Human-readable design name. */
  design_name?: string;
  /** Original product id that was sent when creating the context. */
  product_id?: string;
  /** Original variant id. */
  variant_id?: string;
  /** Key that links this context to a specific line-item row in the order/installation form. */
  line_item_key?: string;
  /** Source flow that created this context. */
  source?: string;
  /** Full order snapshot sent when opening Studio from signage_create_order. */
  order?: Record<string, unknown>;
  /** Full installation snapshot sent when opening Studio from signage_add_installation. */
  installation_order?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Response shape of GET /customization-batches/:batchId */
export interface CustomizationBatchResponse {
  batch_id: string;
  source?: string;
  contexts: CustomizationContextResult[];
  [key: string]: unknown;
}

/**
 * GET /api/v1/customization-batches/:batchId — load stored context after return from Studio.
 */
export async function getCustomizationBatch(batchId: string): Promise<CustomizationBatchResponse> {
  const id = String(batchId ?? "").trim();
  if (!id) {
    throw new Error("Batch id is required");
  }

  const token = getAuthValue("sessionToken");
  if (!token) {
    throw new Error("Session token not found");
  }

  const response = await fetch(customizationApiUrl(`/customization-batches/${encodeURIComponent(id)}`), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const text = await response.text().catch(() => "");
  if (!response.ok) {
    throw new Error(text || `Failed to load customization batch (${response.status})`);
  }

  try {
    return text ? (JSON.parse(text) as CustomizationBatchResponse) : ({ batch_id: batchId, contexts: [] } as CustomizationBatchResponse);
  } catch {
    return { batch_id: batchId, contexts: [] } as CustomizationBatchResponse;
  }
}
